// =====================================================================
// WeddingOS – /dj  (DJ-View)
// =====================================================================
// Fokussierter Vollbild-Screen – kein Menü, kein Burger.
// • Token aus URL (?token=...) oder sessionStorage
// • Musik-Queue in Echtzeit
// • Annehmen/Ablehnen wenn dj_can_moderate=true
// • Klarer Header: Wedding-Name + Moderations-Status
// =====================================================================

import { useState, useEffect, useCallback } from "react";
import { supabase, djDecideRequest } from "../../services/supabase";
import "../../styles/theme.css";

const DJ_TOKEN_KEY = "wos_dj_token";

export default function DJView() {
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) sessionStorage.setItem(DJ_TOKEN_KEY, t);
    return t ?? sessionStorage.getItem(DJ_TOKEN_KEY) ?? null;
  });

  const [ctx,      setCtx]      = useState(null);   // { wedding_id, wedding_name, can_moderate }
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [deciding, setDeciding] = useState(new Set());
  const [error,    setError]    = useState(null);

  // Token → Context + Queue
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data, error: rpcErr } = await supabase.rpc("dj_get_context", { p_token: token });
        if (rpcErr) throw rpcErr;
        const c = Array.isArray(data) ? data[0] : data;
        if (!c) throw new Error("invalid_token");
        setCtx(c);

        const { data: queue } = await supabase
          .from("music_requests")
          .select("*")
          .eq("wedding_id", c.wedding_id)
          .order("created_at", { ascending: true });
        setRequests(queue ?? []);
      } catch (err) {
        setError(err.message?.includes("invalid_token")
          ? "Dieser DJ-Link ist nicht gültig."
          : "Verbindung fehlgeschlagen.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Realtime
  useEffect(() => {
    if (!ctx?.wedding_id) return;
    const ch = supabase
      .channel(`dj-${ctx.wedding_id}`)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "music_requests",
        filter: `wedding_id=eq.${ctx.wedding_id}`,
      }, (p) => {
        setRequests((prev) => {
          if (p.eventType === "INSERT") return [...prev, p.new];
          if (p.eventType === "UPDATE") return prev.map((r) => r.id === p.new.id ? p.new : r);
          return prev;
        });
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [ctx?.wedding_id]);

  const decide = useCallback(async (requestId, approve) => {
    if (!token || !ctx?.can_moderate) return;
    setDeciding((s) => new Set(s).add(requestId));
    try {
      await djDecideRequest({ token, requestId, approve });
      setRequests((prev) =>
        prev.map((r) => r.id === requestId
          ? { ...r, status: approve ? "approved" : "rejected" }
          : r)
      );
    } catch (err) {
      setError(`Fehler: ${err.message}`);
    } finally {
      setDeciding((s) => { const n = new Set(s); n.delete(requestId); return n; });
    }
  }, [token, ctx]);

  if (!token) return <AccessDenied msg="Öffne den DJ-Link, den du vom Brautpaar erhalten hast." />;

  const pending  = requests.filter((r) => r.status === "pending");
  const approved = requests.filter((r) => r.status === "approved");
  const rejected = requests.filter((r) => r.status === "rejected");

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerTop}>
          <div>
            <p style={styles.monogram}>{ctx?.wedding_name ?? "unsereHochzeits.app"}</p>
            <h1 style={styles.title}>DJ Queue</h1>
          </div>
          <div style={styles.headerRight}>
            <LiveDot />
            {ctx && (
              <span style={{
                ...styles.badge,
                background: ctx.can_moderate ? "var(--forest-soft)" : "var(--surface)",
                color:      ctx.can_moderate ? "var(--forest)" : "var(--ink-subtle)",
                border:     `1px solid ${ctx.can_moderate ? "var(--forest)" : "var(--line)"}`,
              }}>
                {ctx.can_moderate ? "MODERATION AN" : "READ ONLY"}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="wos-error" style={{ marginTop: 12 }}>
            {error}
            <button onClick={() => setError(null)} style={styles.dismissBtn}>×</button>
          </div>
        )}
      </header>

      {/* ── Content ── */}
      <main style={styles.content}>
        {loading ? (
          <Skeleton rows={3} />
        ) : requests.length === 0 ? (
          <Empty msg="Noch keine Musikwünsche. Neue erscheinen automatisch." />
        ) : (
          <>
            {pending.length > 0 && (
              <Section label="Ausstehend" count={pending.length} color="var(--accent)">
                {pending.map((r) => (
                  <QueueCard
                    key={r.id} request={r}
                    canModerate={ctx?.can_moderate}
                    busy={deciding.has(r.id)}
                    onApprove={() => decide(r.id, true)}
                    onReject={()  => decide(r.id, false)}
                  />
                ))}
              </Section>
            )}

            {approved.length > 0 && (
              <Section label="In der Playlist" count={approved.length} color="var(--forest)">
                {approved.map((r) => <QueueCard key={r.id} request={r} />)}
              </Section>
            )}

            {rejected.length > 0 && (
              <Collapsible label={`Abgelehnt (${rejected.length})`}>
                {rejected.map((r) => <QueueCard key={r.id} request={r} />)}
              </Collapsible>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── QueueCard ────────────────────────────────────────────────────────

function QueueCard({ request, canModerate, busy, onApprove, onReject }) {
  const isApproved = request.status === "approved";
  const isRejected = request.status === "rejected";
  const isPending  = request.status === "pending";

  return (
    <div style={{
      display:      "flex",
      gap:          12,
      padding:      "13px 14px",
      background:   isApproved ? "var(--forest-soft)" : "var(--bg-elevated)",
      border:       `1px solid ${isApproved ? "var(--forest)" : "var(--line)"}`,
      borderRadius: "var(--radius-md)",
      opacity:      isRejected ? 0.5 : 1,
      transition:   "background 0.3s",
    }}>
      {/* Cover */}
      <div style={{
        width: 46, height: 46, flexShrink: 0,
        borderRadius: "var(--radius-sm)", overflow: "hidden",
        background: "var(--surface)", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {request.album_cover_url
          ? <img src={request.album_cover_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ fontSize: 18, color: "var(--ink-subtle)" }}>♩</span>}
      </div>

      {/* Text */}
      <div style={{ flex: 1, overflow: "hidden", alignSelf: "center" }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {request.track_title}
        </p>
        <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ink-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {request.artist}
        </p>
        {request.message && (
          <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--ink-subtle)", fontStyle: "italic" }}>
            „{request.message}"
          </p>
        )}
      </div>

      {/* Aktionen */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, flexShrink: 0 }}>
        {isPending && canModerate ? (
          busy ? (
            <div className="wos-spinner" style={{ borderColor: "rgba(183,110,121,0.3)", borderTopColor: "var(--accent)" }} />
          ) : (
            <>
              <RoundBtn label="✓" title="Annehmen" color="#fff" bg="var(--forest)" onClick={onApprove} />
              <RoundBtn label="✕" title="Ablehnen" color="var(--ink-muted)" bg="var(--surface)" onClick={onReject} />
            </>
          )
        ) : (
          <span style={{
            fontSize: 11, fontWeight: 700,
            color: isApproved ? "var(--forest)" : isRejected ? "var(--ink-subtle)" : "var(--ink-subtle)",
          }}>
            {isApproved ? "✓" : isRejected ? "✕" : "—"}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Primitives ────────────────────────────────────────────────────────

function Section({ label, count, color, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color, textTransform: "uppercase" }}>{label}</span>
        <span style={{ background: color, color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: "999px", padding: "1px 8px" }}>{count}</span>
        <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} style={{
        display: "flex", alignItems: "center", gap: 8, background: "none", border: "none",
        cursor: "pointer", color: "var(--ink-muted)", fontSize: 13, fontFamily: "var(--font-body)",
        padding: 0, marginBottom: open ? 10 : 0,
      }}>
        <span style={{ transform: open ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>▶</span>
        {label}
      </button>
      {open && <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>}
    </div>
  );
}

function RoundBtn({ label, title, color, bg, onClick }) {
  return (
    <button title={title} onClick={onClick} style={{
      width: 30, height: 30, borderRadius: "50%",
      background: bg, border: "1px solid var(--line)",
      color, fontSize: 12, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer",
    }}>{label}</button>
  );
}

function LiveDot() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--forest)", boxShadow: "0 0 0 2px var(--forest-soft)" }} />
      <span style={{ fontSize: 10, color: "var(--ink-subtle)", letterSpacing: "0.12em" }}>LIVE</span>
    </div>
  );
}

function Skeleton({ rows }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ height: 68, borderRadius: "var(--radius-md)", background: "var(--surface)", opacity: 0.5 }} />
      ))}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div style={{ textAlign: "center", paddingTop: 80 }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", color: "var(--ink-muted)" }}>Keine Wünsche</p>
      <p style={{ fontSize: 13, color: "var(--ink-subtle)", marginTop: 8 }}>{msg}</p>
    </div>
  );
}

function AccessDenied({ msg }) {
  return (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)", fontFamily: "var(--font-body)" }}>
      <div className="wos-card" style={{ maxWidth: 340, textAlign: "center" }}>
        <p style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem" }}>Kein Zugang</p>
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-muted)" }}>{msg}</p>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight:     "100dvh",
    background:    "var(--bg)",
    fontFamily:    "var(--font-body)",
    color:         "var(--ink)",
    display:       "flex",
    flexDirection: "column",
    maxWidth:      640,
    margin:        "0 auto",
    width:         "100%",
  },
  header: {
    padding:      "20px 20px 16px",
    borderBottom: "1px solid var(--line)",
    background:   "var(--bg-elevated)",
    position:     "sticky",
    top:          0,
    zIndex:       10,
  },
  headerTop: {
    display:        "flex",
    justifyContent: "space-between",
    alignItems:     "flex-start",
    gap:            12,
  },
  headerRight: {
    display:    "flex",
    alignItems: "center",
    gap:        10,
    flexShrink: 0,
    marginTop:  4,
  },
  monogram: {
    fontFamily:    "var(--font-display)",
    fontStyle:     "italic",
    fontSize:      11,
    letterSpacing: "0.25em",
    color:         "var(--accent)",
    margin:        "0 0 4px",
  },
  title: {
    fontFamily: "var(--font-display)",
    fontSize:   "1.6rem",
    fontWeight: 500,
    margin:     0,
  },
  badge: {
    fontSize:      10,
    fontWeight:    700,
    letterSpacing: "0.1em",
    borderRadius:  "999px",
    padding:       "4px 10px",
  },
  content: {
    flex:    1,
    padding: "20px 20px 60px",
  },
  dismissBtn: {
    background: "none", border: "none", cursor: "pointer",
    float: "right", color: "var(--accent-deep)", fontWeight: 700,
  },
};
